import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "@/data/spa";
import { useLang } from "@/i18n/LanguageContext";
import { AmbientMedia } from "@/components/spa/AmbientMedia";
import { TiltCard } from "@/components/spa/TiltCard";

const numerals = ["I", "II", "III"];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Philosophy = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY1 = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const imgY2 = useTransform(scrollYProgress, [0, 1], [50, -20]);

  return (
    <section ref={ref} id="philosophy" data-testid="philosophy-section" className="relative py-24 lg:py-40 overflow-hidden">
      <span aria-hidden="true" className="ghost-char top-6 left-2">靜</span>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-8 items-start">
        <div className="lg:col-span-6">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}>
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-12 bg-[#c5a059]" />
              <span className="text-[11px] tracking-[0.4em] uppercase text-[#c5a059]">{t.philosophy.label}</span>
            </div>
            <h2 className="font-serif-display font-medium text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-[#f0f4f2] max-w-xl">
              {t.philosophy.title.pre}<span className="italic text-[#c5a059]">{t.philosophy.title.it}</span>{t.philosophy.title.post}
            </h2>
            <p className="mt-8 text-[#8a9690] font-light leading-relaxed max-w-lg text-base">
              {t.philosophy.body}
            </p>
          </motion.div>

          <div className="mt-16 space-y-4">
            {t.philosophy.pillars.map((p, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
              >
                <TiltCard
                  testid={`philosophy-pillar-${i + 1}`}
                  max={3}
                  lift={4}
                  glow="0 18px 50px -18px rgba(197,160,89,0.35)"
                  className="group flex gap-8 py-8 border border-white/10 hover:border-[#c5a059]/30 bg-[#0c100e]/40 hover:bg-white/[0.02] transition-colors duration-500 px-6"
                >
                  <span className="font-serif-display italic text-2xl text-[#c5a059] w-10 shrink-0 pt-1">
                    {numerals[i]}
                  </span>
                  <div>
                    <h3 className="font-serif-display text-2xl text-[#f0f4f2] group-hover:text-[#dcc286] transition-colors duration-300">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-sm text-[#8a9690] font-light leading-relaxed max-w-md">{p.text}</p>
                  </div>
                </TiltCard>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6 relative h-[520px] sm:h-[640px] lg:h-[780px]">
          <motion.div style={{ y: imgY1 }} className="absolute top-0 right-0 w-[68%] h-[58%]">
            <TiltCard
              testid="philosophy-media-01"
              max={3}
              lift={4}
              glow="0 26px 65px -20px rgba(197,160,89,0.5)"
              className="w-full h-full overflow-hidden"
            >
              <motion.div
                initial={{ scale: 1.15 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                className="w-full h-full"
              >
                <AmbientMedia
                  video="/rituals/v8.mp4"
                  poster={IMAGES.steamRoom}
                  alt="Warm oil pour ritual"
                  className="w-full h-full object-cover block"
                />
              </motion.div>
            </TiltCard>
          </motion.div>
          <motion.div style={{ y: imgY2 }} className="absolute bottom-0 left-0 w-[62%] h-[54%] z-10">
            <TiltCard
              testid="philosophy-media-02"
              max={3}
              lift={4}
              glow="0 26px 65px -20px rgba(197,160,89,0.5)"
              className="w-full h-full overflow-hidden border-[8px] border-[#050706]"
            >
              <motion.div
                initial={{ scale: 1.15 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                className="w-full h-full"
              >
                <AmbientMedia
                  video="/rituals/v7.mp4"
                  poster={IMAGES.headMassage}
                  alt="Head & scalp ritual — hands at work"
                  className="w-full h-full object-cover block"
                />
              </motion.div>
            </TiltCard>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, rotate: -20 }}
            whileInView={{ opacity: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.4 }}
            className="absolute top-[54%] right-[8%] z-20"
          >
            <div className="animate-spin-slow w-28 h-28 rounded-full border border-[#c5a059]/40 flex items-center justify-center">
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <defs>
                  <path id="circlePath" d="M 50,50 m -34,0 a 34,34 0 1,1 68,0 a 34,34 0 1,1 -68,0" />
                </defs>
                <text fill="#c5a059" fontSize="9.5" letterSpacing="2.5">
                  <textPath href="#circlePath">ELITE SPA · LAKEWOOD · EST 2026 ·</textPath>
                </text>
              </svg>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
